import { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { ROLE_LEVEL, ROLE_LABELS } from '../../utils/constants';
import { Users, Plus, Trash2, UserCheck, UserX, Loader2, ShieldCheck } from 'lucide-react';
import { SkeletonCard } from '../../components/common/SkeletonCard';

const ROLES = ['super_admin', 'admin', 'org_admin', 'team_lead', 'editor', 'trainer', 'viewer'];

const StatCard = ({ label, value, accent }) => (
  <div className="rounded-lg border p-4 bg-card">
    <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
    <div className={`text-2xl font-semibold mt-1 ${accent || ''}`}>{value}</div>
  </div>
);

const RoleBadge = ({ role }) => {
  const tone = role === 'super_admin'
    ? 'bg-purple-100 text-purple-700'
    : role === 'admin'
      ? 'bg-indigo-100 text-indigo-700'
      : role === 'org_admin'
        ? 'bg-warm-200 text-warm-800'
        : role === 'team_lead'
          ? 'bg-amber-100 text-amber-700'
          : role === 'editor'
            ? 'bg-warm-200 text-warm-800'
            : role === 'trainer'
              ? 'bg-teal-100 text-teal-700'
              : role === 'viewer'
                ? 'bg-slate-100 text-slate-600'
                : role === 'editor_trainer'   // legacy display only
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-muted text-muted-foreground';
  return <Badge variant="secondary" className={`${tone} hover:${tone}`}>{ROLE_LABELS[role] || role}</Badge>;
};

export default function UsersAccessTab() {
  const { get, post, put, del } = useApi();
  const { user, impersonatingRole } = useAuth();
  const effectiveRole = impersonatingRole || user?.role;
  const isPlatformAdmin = (ROLE_LEVEL[effectiveRole] || 0) >= ROLE_LEVEL.admin;
  const isSuperAdmin = effectiveRole === 'super_admin';

  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [orgFilter, setOrgFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const limit = 50;

  const [selected, setSelected] = useState(() => new Set());
  const [editing, setEditing] = useState(null);
  const [pendingBulk, setPendingBulk] = useState(null);
  const [busy, setBusy] = useState(false);

  // Fetch organizations once for filter dropdown (platform_admin+)
  useEffect(() => {
    if (!isPlatformAdmin) return;
    get('/organizations').then(r => setOrgs(r.organizations || [])).catch(() => {});
  }, [isPlatformAdmin, get]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (orgFilter) params.set('org_id', orgFilter);
      if (roleFilter) params.set('role', roleFilter);
      if (statusFilter) params.set('is_active', statusFilter === 'active' ? 'true' : 'false');
      if (search.trim()) params.set('search', search.trim());
      params.set('skip', String(page * limit));
      params.set('limit', String(limit));

      // platform_admin+ uses cross-org endpoint; org_admin uses scoped /users
      const path = isPlatformAdmin ? `/users/cross-org?${params}` : `/users?${params}`;
      const res = await get(path);
      setUsers(res.users || []);
      setTotal(res.pagination?.total || 0);
      setSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [get, isPlatformAdmin, orgFilter, roleFilter, statusFilter, search, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const stats = useMemo(() => {
    const admins = users.filter(u => ['super_admin', 'admin', 'org_admin'].includes(u.role)).length;
    const active = users.filter(u => u.is_active).length;
    return { admins, active };
  }, [users]);

  const allSelected = users.length > 0 && users.every(u => selected.has(u.user_id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(users.map(u => u.user_id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const runBulk = async (action) => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const res = await post('/users/bulk-action', { user_ids: Array.from(selected), action });
      toast.success(`${action}: ${res.succeeded} succeeded, ${res.failed} failed`);
      if (res.errors?.length) toast.warning(res.errors[0]);
      setPendingBulk(null);
      await fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSingleDelete = async (u) => {
    setBusy(true);
    try {
      await del(`/users/${u.user_id}`);
      toast.success(`Deleted ${u.email}`);
      await fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading && users.length === 0) {
    return <div className="space-y-4">{['s1','s2','s3'].map(k => <SkeletonCard key={k} />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="settings-users">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total users" value={total} />
        <StatCard label="Active (this page)" value={stats.active} accent="text-emerald-600" />
        <StatCard label="Admins (this page)" value={stats.admins} accent="text-indigo-600" />
        <StatCard label="Selected" value={selected.size} accent={selected.size ? 'text-amber-600' : ''} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Input
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="max-w-xs"
          data-testid="users-search"
        />
        {isPlatformAdmin && (
          <select
            value={orgFilter}
            onChange={(e) => { setOrgFilter(e.target.value); setPage(0); }}
            className="border rounded-md px-3 py-2 text-sm bg-background h-9"
            data-testid="users-org-filter"
          >
            <option value="">All organizations</option>
            {orgs.map(o => <option key={o.org_id} value={o.org_id}>{o.name}</option>)}
          </select>
        )}
        <select
          value={roleFilter}
          onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}
          className="border rounded-md px-3 py-2 text-sm bg-background h-9"
          data-testid="users-role-filter"
        >
          <option value="">All roles</option>
          {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="border rounded-md px-3 py-2 text-sm bg-background h-9"
          data-testid="users-status-filter"
        >
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <div className="flex-1" />
        <Button onClick={() => setEditing({})} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" /> Add user
        </Button>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-lg border p-3 bg-muted/40" data-testid="users-bulk-bar">
          <div className="text-sm">
            <strong>{selected.size}</strong> selected
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPendingBulk('activate')} data-testid="bulk-activate-btn">
              <UserCheck className="w-4 h-4 mr-1" /> Activate
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPendingBulk('deactivate')} data-testid="bulk-deactivate-btn">
              <UserX className="w-4 h-4 mr-1" /> Deactivate
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setPendingBulk('delete')} data-testid="bulk-delete-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-3 w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" data-testid="select-all-users" />
              </th>
              <th className="text-left p-3 font-medium">User</th>
              <th className="text-left p-3 font-medium hidden md:table-cell">Role</th>
              {isPlatformAdmin && <th className="text-left p-3 font-medium hidden lg:table-cell">Organization</th>}
              <th className="text-left p-3 font-medium hidden md:table-cell">Status</th>
              <th className="text-left p-3 font-medium hidden lg:table-cell">Last login</th>
              <th className="text-right p-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={isPlatformAdmin ? 7 : 6} className="p-8 text-center text-muted-foreground">No users match these filters.</td></tr>
            )}
            {users.map(u => {
              const isSelf = u.user_id === user?.user_id;
              const lockedFromMe = (u.role === 'super_admin' || u.role === 'admin') && !isSuperAdmin;
              return (
                <tr key={u.user_id} className="border-t hover:bg-muted/30" data-testid="user-row">
                  <td className="p-3">
                    <Checkbox
                      checked={selected.has(u.user_id)}
                      onCheckedChange={() => toggleOne(u.user_id)}
                      disabled={isSelf}
                      aria-label={`Select ${u.email}`}
                      data-testid={`select-user-${u.user_id}`}
                    />
                  </td>
                  <td className="p-3">
                    <div className="font-medium">{u.first_name} {u.last_name} {isSelf && <span className="text-xs text-muted-foreground">(you)</span>}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </td>
                  <td className="p-3 hidden md:table-cell"><RoleBadge role={u.role} /></td>
                  {isPlatformAdmin && (
                    <td className="p-3 hidden lg:table-cell text-muted-foreground">{u.org_name || '—'}</td>
                  )}
                  <td className="p-3 hidden md:table-cell">
                    {u.is_active
                      ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
                      : <Badge variant="secondary" className="bg-amber-100 text-amber-700">Inactive</Badge>}
                  </td>
                  <td className="p-3 hidden lg:table-cell text-muted-foreground text-xs">
                    {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(u)} disabled={lockedFromMe} data-testid={`edit-user-${u.user_id}`}>
                        <ShieldCheck className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleSingleDelete(u)}
                        disabled={isSelf || lockedFromMe || busy}
                        data-testid={`delete-user-${u.user_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>Showing {users.length} of {total}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Prev</Button>
          <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      </div>

      <UserEditDialog
        open={!!editing}
        target={editing}
        orgs={orgs}
        isPlatformAdmin={isPlatformAdmin}
        defaultOrgId={user?.org_id}
        onClose={() => setEditing(null)}
        onSaved={fetchUsers}
        post={post}
        put={put}
      />

      <AlertDialog open={!!pendingBulk} onOpenChange={(o) => !o && !busy && setPendingBulk(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingBulk ? pendingBulk[0].toUpperCase() + pendingBulk.slice(1) : ''} {selected.size} user(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingBulk === 'delete'
                ? 'This will permanently delete the selected accounts. This cannot be undone.'
                : pendingBulk === 'deactivate'
                  ? 'Deactivated users will be blocked from logging in. Reversible.'
                  : 'Selected users will be reactivated and able to log in again.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => runBulk(pendingBulk)}
              disabled={busy}
              className={pendingBulk === 'delete' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
              data-testid="confirm-bulk-action-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UserEditDialog({ open, target, orgs, isPlatformAdmin, defaultOrgId, onClose, onSaved, post, put }) {
  const isNew = !target?.user_id;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) {
      setForm({
        email: target.email || '',
        first_name: target.first_name || '',
        last_name: target.last_name || '',
        role: target.role || 'trainer',
        department: target.department || '',
        job_title: target.job_title || '',
        org_id: target.org_id || defaultOrgId || '',
        is_active: target.is_active !== false,
      });
    }
  }, [target, defaultOrgId]);

  if (!open) return null;

  const submit = async () => {
    if (!form.email || !form.first_name || !form.last_name) {
      toast.error('Email, first name, and last name are required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const payload = {
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
          role: form.role,
          department: form.department || null,
          job_title: form.job_title || null,
        };
        if (isPlatformAdmin && form.org_id) payload.org_id = form.org_id;
        await post('/users', payload);
        toast.success(`Created ${form.email}`);
      } else {
        await put(`/users/${target.user_id}`, {
          first_name: form.first_name,
          last_name: form.last_name,
          role: form.role,
          department: form.department || null,
          job_title: form.job_title || null,
          is_active: form.is_active,
        });
        toast.success('Saved');
      }
      onClose();
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent data-testid="user-edit-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add user' : `Edit ${target.email}`}</DialogTitle>
          <DialogDescription>
            {isNew ? 'New user will receive a sign-in code via email when they first log in.' : 'Profile, role, and status. Email cannot be changed.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>First name</Label>
              <Input value={form.first_name || ''} onChange={(e) => setForm({...form, first_name: e.target.value})} data-testid="user-firstname-input" />
            </div>
            <div>
              <Label>Last name</Label>
              <Input value={form.last_name || ''} onChange={(e) => setForm({...form, last_name: e.target.value})} data-testid="user-lastname-input" />
            </div>
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email || ''} onChange={(e) => setForm({...form, email: e.target.value})} disabled={!isNew} data-testid="user-email-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Role</Label>
              <select
                value={form.role || 'trainer'}
                onChange={(e) => setForm({...form, role: e.target.value})}
                className="w-full border rounded-md px-3 py-2 text-sm bg-background h-9"
                data-testid="user-role-select"
              >
                {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
              </select>
            </div>
            <div>
              <Label>Status</Label>
              <select
                value={form.is_active ? 'true' : 'false'}
                onChange={(e) => setForm({...form, is_active: e.target.value === 'true'})}
                disabled={isNew}
                className="w-full border rounded-md px-3 py-2 text-sm bg-background h-9"
                data-testid="user-status-select"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          {isNew && isPlatformAdmin && (
            <div>
              <Label>Organization</Label>
              <select
                value={form.org_id || ''}
                onChange={(e) => setForm({...form, org_id: e.target.value})}
                className="w-full border rounded-md px-3 py-2 text-sm bg-background h-9"
                data-testid="user-org-select"
              >
                {orgs.map(o => <option key={o.org_id} value={o.org_id}>{o.name}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Department</Label>
              <Input value={form.department || ''} onChange={(e) => setForm({...form, department: e.target.value})} data-testid="user-department-input" />
            </div>
            <div>
              <Label>Job title</Label>
              <Input value={form.job_title || ''} onChange={(e) => setForm({...form, job_title: e.target.value})} data-testid="user-jobtitle-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="user-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
